import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  Plus,
  Trash2,
  Play,
  Pause,
  Square,
  ChevronRight,
  ArrowLeft,
  Sparkles,
  Workflow,
  Users,
  Save,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { http, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const TONES = ["consultative", "direct", "friendly"];

export default function SequencesPanel() {
  const [view, setView] = useState("list"); // list | detail
  const [sequences, setSequences] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [creating, setCreating] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [seq, tpl] = await Promise.all([
        http.get("/sequences").then((r) => r.data.sequences || []),
        http.get("/sequences/templates").then((r) => r.data.templates || []),
      ]);
      setSequences(seq);
      setTemplates(tpl);
    } catch (e) {
      toast.error("Failed to load sequences");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const onCreate = async (name, templateKey) => {
    setCreating(true);
    try {
      const { data } = await http.post("/sequences", { name, template: templateKey || null });
      toast.success(`Created "${data.sequence.name}"`);
      await loadAll();
      setActiveId(data.sequence.id);
      setView("detail");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not create sequence");
    } finally {
      setCreating(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this sequence? Active enrollments will be stopped.")) return;
    try {
      await http.delete(`/sequences/${id}`);
      toast.success("Sequence deleted");
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to delete");
    }
  };

  if (view === "detail" && activeId) {
    return (
      <SequenceDetail
        id={activeId}
        onBack={() => {
          setView("list");
          loadAll();
        }}
      />
    );
  }

  return (
    <div data-testid="sequences-panel">
      <CreateSequenceCard templates={templates} busy={creating} onCreate={onCreate} />

      <div className="mt-8">
        <h3 className="font-display text-lg font-bold text-ink-900 mb-3">Your sequences</h3>
        {loading ? (
          <div className="grid place-items-center py-12 text-ink-400 text-sm">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : sequences.length === 0 ? (
          <div className="border border-dashed border-ink-200 rounded-sm p-10 text-center bg-white" data-testid="sequences-empty">
            <Workflow className="w-6 h-6 mx-auto text-ink-300 mb-2" />
            <p className="text-ink-500 text-sm">
              No sequences yet — start from a template above to send multi-step
              follow-ups automatically when prospects don't reply.
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="sequences-list">
            {sequences.map((s) => (
              <div
                key={s.id}
                className="bg-white border border-ink-100 rounded-sm p-4 flex items-center justify-between hover:border-ink-300 transition-colors"
                data-testid={`sequence-row-${s.id}`}
              >
                <button
                  onClick={() => {
                    setActiveId(s.id);
                    setView("detail");
                  }}
                  className="flex-1 text-left flex items-center gap-3 min-w-0"
                >
                  <span
                    className={`w-2 h-2 rounded-full ${
                      s.status === "active"
                        ? "bg-green-500"
                        : s.status === "paused"
                        ? "bg-amber-500"
                        : "bg-ink-300"
                    }`}
                  />
                  <div className="min-w-0">
                    <div className="font-semibold text-ink-900 truncate">{s.name}</div>
                    <div className="text-[11px] uppercase tracking-widestx font-mono text-ink-400 mt-0.5">
                      {s.status} · {s.steps_count} step{s.steps_count === 1 ? "" : "s"}
                    </div>
                  </div>
                </button>
                <div className="flex items-center gap-1 ml-3">
                  <button
                    onClick={() => onDelete(s.id)}
                    className="p-2 text-ink-400 hover:text-red-600 transition-colors"
                    data-testid={`sequence-delete-${s.id}`}
                    aria-label="Delete sequence"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <ChevronRight className="w-4 h-4 text-ink-300" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CreateSequenceCard({ templates, busy, onCreate }) {
  const [name, setName] = useState("");
  const [tplKey, setTplKey] = useState("");

  return (
    <div className="bg-white border border-ink-100 rounded-sm p-6" data-testid="sequence-create-card">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-brand" />
        <h3 className="font-display text-lg font-bold text-ink-900">New sequence</h3>
      </div>
      <p className="text-ink-500 text-sm mb-4">
        Start blank or pick a template. You can edit the steps right after.
      </p>

      <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
        <div>
          <Label htmlFor="seq-name" className="text-[11px] uppercase tracking-widestx font-mono text-ink-400">
            Name
          </Label>
          <Input
            id="seq-name"
            data-testid="sequence-name-input"
            placeholder="e.g. Q2 outbound — North America"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1"
          />
        </div>
        <Button
          data-testid="sequence-create-btn"
          disabled={!name.trim() || busy}
          onClick={() => onCreate(name.trim(), tplKey)}
          className="rounded-sm"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Create
        </Button>
      </div>

      <div className="mt-4">
        <div className="text-[11px] uppercase tracking-widestx font-mono text-ink-400 mb-2">Template</div>
        <div className="grid sm:grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => setTplKey("")}
            data-testid="template-blank"
            className={`border rounded-sm p-3 text-left text-sm transition-colors ${
              tplKey === ""
                ? "border-ink-900 bg-ink-50"
                : "border-ink-100 hover:border-ink-300"
            }`}
          >
            <div className="font-semibold text-ink-900">Blank</div>
            <div className="text-ink-500 text-xs mt-0.5">Start with no steps</div>
          </button>
          {templates.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTplKey(t.key)}
              data-testid={`template-${t.key}`}
              className={`border rounded-sm p-3 text-left text-sm transition-colors ${
                tplKey === t.key
                  ? "border-ink-900 bg-ink-50"
                  : "border-ink-100 hover:border-ink-300"
              }`}
            >
              <div className="font-semibold text-ink-900">{t.name}</div>
              <div className="text-ink-500 text-xs mt-0.5">{t.steps_count} steps</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SequenceDetail({ id, onBack }) {
  const [seq, setSeq] = useState(null);
  const [steps, setSteps] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [savingSteps, setSavingSteps] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [seqRes, stepsRes, enrRes] = await Promise.all([
        http.get(`/sequences`).then((r) => (r.data.sequences || []).find((s) => s.id === id)),
        http.get(`/sequences/${id}/steps`).then((r) => r.data.steps || []),
        http.get(`/sequences/${id}/enrollments`).then((r) => r.data.enrollments || []),
      ]);
      setSeq(seqRes);
      setSteps(stepsRes);
      setEnrollments(enrRes);
    } catch (e) {
      toast.error("Failed to load sequence");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const saveSteps = async () => {
    // ensure ascending day_offset
    const sorted = steps
      .map((s, i) => ({ ...s, order: i }))
      .sort((a, b) => a.order - b.order);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].day_offset < sorted[i - 1].day_offset) {
        toast.error("Step day offsets must be ascending");
        return;
      }
    }
    setSavingSteps(true);
    try {
      await http.put(`/sequences/${id}/steps`, {
        steps: sorted.map((s) => ({
          order: s.order,
          day_offset: Number(s.day_offset),
          tone: s.tone,
          subject_hint: s.subject_hint || "",
          body_hint: s.body_hint || "",
        })),
      });
      toast.success("Steps saved");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to save steps");
    } finally {
      setSavingSteps(false);
    }
  };

  const addStep = () => {
    const last = steps[steps.length - 1];
    setSteps([
      ...steps,
      {
        order: steps.length,
        day_offset: last ? Number(last.day_offset) + 3 : 0,
        tone: "consultative",
        subject_hint: "",
        body_hint: "",
      },
    ]);
  };

  const updateStep = (i, patch) => {
    setSteps(steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  };

  const removeStep = (i) => {
    setSteps(steps.filter((_, idx) => idx !== i));
  };

  const toggleStatus = async () => {
    if (!seq) return;
    const next = seq.status === "active" ? "paused" : "active";
    try {
      await http.patch(`/sequences/${id}`, { status: next });
      toast.success(`Sequence ${next}`);
      await load();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  if (loading || !seq) {
    return (
      <div className="grid place-items-center py-16 text-ink-400 text-sm">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="sequence-detail">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-ink-900 mb-6"
        data-testid="sequence-back-btn"
      >
        <ArrowLeft className="w-4 h-4" /> Back to sequences
      </button>

      <div className="bg-white border border-ink-100 rounded-sm p-6 mb-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-widestx font-mono text-ink-400">
              Sequence
            </div>
            <h2 className="font-display text-2xl font-bold text-ink-900 mt-0.5">{seq.name}</h2>
            <div className="text-ink-500 text-sm mt-1">
              {seq.steps_count} step{seq.steps_count === 1 ? "" : "s"} · {enrollments.length} enrollment
              {enrollments.length === 1 ? "" : "s"}
            </div>
          </div>
          <Button
            variant="outline"
            onClick={toggleStatus}
            className="rounded-sm"
            data-testid="sequence-toggle-status"
          >
            {seq.status === "active" ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {seq.status === "active" ? "Pause" : "Activate"}
          </Button>
        </div>
      </div>

      {/* Step builder */}
      <div className="bg-white border border-ink-100 rounded-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-base font-bold text-ink-900">Steps</h3>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={addStep} className="rounded-sm" data-testid="add-step-btn">
              <Plus className="w-4 h-4" /> Add step
            </Button>
            <Button onClick={saveSteps} disabled={savingSteps} className="rounded-sm" data-testid="save-steps-btn">
              {savingSteps ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save steps
            </Button>
          </div>
        </div>

        {steps.length === 0 ? (
          <p className="text-ink-500 text-sm py-6 text-center">
            No steps yet. Click <span className="font-semibold text-ink-900">Add step</span> to begin.
          </p>
        ) : (
          <div className="space-y-3" data-testid="steps-list">
            {steps.map((s, i) => (
              <StepRow
                key={i}
                index={i}
                step={s}
                onChange={(patch) => updateStep(i, patch)}
                onRemove={() => removeStep(i)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Enrollments */}
      <EnrollmentsTable enrollments={enrollments} onChange={load} />
    </div>
  );
}

function StepRow({ index, step, onChange, onRemove }) {
  return (
    <div
      className="border border-ink-100 rounded-sm p-4 grid grid-cols-12 gap-3 items-start"
      data-testid={`step-row-${index}`}
    >
      <div className="col-span-1 text-center">
        <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-1">Step</div>
        <div className="font-display text-2xl font-bold text-ink-900">{index + 1}</div>
      </div>
      <div className="col-span-2">
        <Label className="text-[11px] uppercase tracking-widestx font-mono text-ink-400">Day</Label>
        <Input
          type="number"
          min="0"
          max="180"
          value={step.day_offset}
          onChange={(e) => onChange({ day_offset: e.target.value })}
          className="mt-1"
          data-testid={`step-day-${index}`}
        />
      </div>
      <div className="col-span-2">
        <Label className="text-[11px] uppercase tracking-widestx font-mono text-ink-400">Tone</Label>
        <select
          value={step.tone}
          onChange={(e) => onChange({ tone: e.target.value })}
          className="mt-1 w-full h-9 px-2 border border-ink-200 rounded-sm text-sm bg-white"
          data-testid={`step-tone-${index}`}
        >
          {TONES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
      <div className="col-span-3">
        <Label className="text-[11px] uppercase tracking-widestx font-mono text-ink-400">
          Subject hint
        </Label>
        <Input
          value={step.subject_hint || ""}
          onChange={(e) => onChange({ subject_hint: e.target.value })}
          placeholder="specific signal hook"
          className="mt-1"
          data-testid={`step-subject-${index}`}
        />
      </div>
      <div className="col-span-3">
        <Label className="text-[11px] uppercase tracking-widestx font-mono text-ink-400">
          Body hint
        </Label>
        <Input
          value={step.body_hint || ""}
          onChange={(e) => onChange({ body_hint: e.target.value })}
          placeholder="case study angle"
          className="mt-1"
          data-testid={`step-body-${index}`}
        />
      </div>
      <div className="col-span-1 grid place-items-center pt-5">
        <button
          onClick={onRemove}
          className="p-2 text-ink-400 hover:text-red-600 transition-colors"
          aria-label="Remove step"
          data-testid={`step-remove-${index}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function EnrollmentsTable({ enrollments, onChange }) {
  const action = async (id, kind) => {
    try {
      await http.post(`/enrollments/${id}/${kind}`);
      toast.success(`Enrollment ${kind}d`);
      onChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed");
    }
  };

  if (!enrollments?.length) {
    return (
      <div className="bg-white border border-ink-100 rounded-sm p-6" data-testid="enrollments-empty">
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-4 h-4 text-ink-400" />
          <h3 className="font-display text-base font-bold text-ink-900">Enrolled leads</h3>
        </div>
        <p className="text-ink-500 text-sm">
          No leads enrolled yet. From the Dashboard, expand any lead and click
          <span className="mx-1 font-semibold text-ink-900">Enroll in sequence</span>
          to start.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-ink-100 rounded-sm p-6">
      <div className="flex items-center gap-2 mb-4">
        <Users className="w-4 h-4 text-ink-400" />
        <h3 className="font-display text-base font-bold text-ink-900">Enrolled leads</h3>
        <span className="text-[11px] uppercase tracking-widestx font-mono text-ink-400">
          ({enrollments.length})
        </span>
      </div>
      <div className="divide-y divide-ink-100" data-testid="enrollments-list">
        {enrollments.map((e) => (
          <div
            key={e.id}
            className="py-3 flex items-center justify-between gap-3"
            data-testid={`enrollment-row-${e.id}`}
          >
            <div className="min-w-0">
              <div className="text-sm font-mono text-ink-700 truncate">{e.lead_id}</div>
              <div className="text-[11px] uppercase tracking-widestx font-mono text-ink-400 mt-0.5">
                {e.status} · step {e.current_step_index + 1}
                {e.next_due_at && ` · next ${new Date(e.next_due_at).toLocaleString()}`}
                {e.pause_reason && ` · ${e.pause_reason}`}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {e.status === "running" && (
                <button
                  onClick={() => action(e.id, "pause")}
                  className="p-2 text-ink-400 hover:text-amber-600 transition-colors"
                  aria-label="Pause"
                  data-testid={`enrollment-pause-${e.id}`}
                >
                  <Pause className="w-4 h-4" />
                </button>
              )}
              {e.status === "paused" && (
                <button
                  onClick={() => action(e.id, "resume")}
                  className="p-2 text-ink-400 hover:text-green-600 transition-colors"
                  aria-label="Resume"
                  data-testid={`enrollment-resume-${e.id}`}
                >
                  <Play className="w-4 h-4" />
                </button>
              )}
              {(e.status === "running" || e.status === "paused") && (
                <button
                  onClick={() => action(e.id, "stop")}
                  className="p-2 text-ink-400 hover:text-red-600 transition-colors"
                  aria-label="Stop"
                  data-testid={`enrollment-stop-${e.id}`}
                >
                  <Square className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
